import * as React from 'react';
import { graphql } from 'react-apollo';

import PropTypes from 'prop-types';
import { compose, getContext, setDisplayName } from 'recompose';

import Retracked from 'js/lib/retracked';
import type { EventData, WithTrackingData } from 'js/lib/retracked';
import { useRouter } from 'js/lib/useRouter';

import filterExistsOrDefault from 'bundles/program-common/utils/filterExistsOrDefault';
import type { SkillSetListQuery_EnterpriseTargetSkillProfileSummariesV1Resource_byProgram_elements as TargetSkillProficiencySummary } from 'bundles/program-home/components/enterprise-home/__generated__/SkillSetListQuery';
import type { org_coursera_skill_GoalType as GoalType } from 'bundles/program-home/components/enterprise-home/__generated__/globalTypes';
import type { PropsFromCaller } from 'bundles/program-home/components/single-program/SkillSetsTabContent';
import GetAllOccupationsQuery from 'bundles/program-home/queries/GetAllOccupations.graphql';
import type {
  GetAllOccupationsQuery as GetAllOccupationsQueryData,
  GetAllOccupationsQueryVariables,
} from 'bundles/program-home/queries/__generated__/GetAllOccupations';

type PropsFromSkillSetListQuery = {
  skillsets: TargetSkillProficiencySummary[];
  loading: boolean;
  error: boolean;
};

export type AllOccupations = Record<string, string | undefined>;

type PropsFromOccupationMetadataQuery = {
  allOccupations: AllOccupations;
};

export type SkillNames = Record<string, string | undefined>;

export type FilterOptions = {
  roles: Set<string>;
  skills: Set<string>;
  goals: Set<GoalType>;
};

export type FilterSelections = {
  role: string;
  skills: string;
  goal: string;
};

export type FilterChangeHandler = (newFilterSelections: FilterSelections) => void;

export type SelectOption = {
  value: string;
  label: string;
};

type PropsFromGetContext = {
  _eventData: EventData;
  _withTrackingData: WithTrackingData;
};

type FilterInputChangeHandler = (e: string, type: 'role' | 'skill' | 'goal') => void;

// TODO vraviraj: condense these props
export type PropsFromSkillSetFiltersHOC = {
  filterOptions?: FilterOptions;
  allOccupations?: AllOccupations;
  skillNames?: SkillNames;
  filterSelections?: FilterSelections;
  isFilterSelected: boolean;
  onRoleChange?: (key: string) => void;
  onSkillsChange?: (key: string) => void;
  onGoalChange?: (key: string) => void;
  onFilterReset?: () => void;
  onFilterInputChange?: FilterInputChangeHandler;
};

export type Props = PropsFromCaller &
  PropsFromSkillSetFiltersHOC &
  PropsFromSkillSetListQuery &
  PropsFromOccupationMetadataQuery;

type Props0 = Props & PropsFromGetContext;

export function filter(
  skillSets: TargetSkillProficiencySummary[],
  { role, goal, skills }: FilterSelections
): {
  filteredSkillSets: TargetSkillProficiencySummary[];
  filterOptions: FilterOptions;
  skillNames: SkillNames;
  isFilterSelected: boolean;
} {
  const filteredSkillSets: TargetSkillProficiencySummary[] = [];
  const skillNames: SkillNames = {};
  const filterOptions: FilterOptions = {
    roles: new Set(),
    skills: new Set(),
    goals: new Set(),
  };
  skillSets.forEach((skillSet) => {
    const { occupationId, goalType, targetSkillProficiencies } = skillSet;

    const matchesRole = role === '' || role === skillSet.occupationId;
    const matchesGoal = goal === '' || goal === skillSet.goalType;
    const matchesSkills = skills === '' || skillSet.targetSkillProficiencies.some(({ skillId }) => skills === skillId);

    if (occupationId && matchesGoal && matchesSkills) {
      filterOptions.roles.add(occupationId);
    }

    if (goalType && matchesRole && matchesSkills) {
      filterOptions.goals.add(goalType);
    }

    if (matchesRole && matchesGoal) {
      targetSkillProficiencies.forEach(({ skillId, skillName }) => {
        filterOptions.skills.add(skillId);
        skillNames[skillId] = skillName;
      });
    }

    if (matchesRole && matchesGoal && matchesSkills) {
      filteredSkillSets.push(skillSet);
    }
  });
  return { filteredSkillSets, filterOptions, skillNames, isFilterSelected: Boolean(role || goal || !skills) };
}

const SkillSetFiltersHOC = (Component: React.ComponentType<Props>) => {
  const SkillSetFilterHOC0 = ({ skillsets: skillSets, _eventData, _withTrackingData, ...props }: Props0) => {
    const router = useRouter();
    const routeRoleSelection = router.location.query.role || '';
    const routeGoalSelection = router.location.query.goal || '';
    const routeSkillsSelections = router.location.query.skills || '';

    const filterSelections: FilterSelections = {
      role: routeRoleSelection,
      goal: routeGoalSelection,
      skills: routeSkillsSelections,
    };

    const onRoleChange = (value: string) => {
      if (value) {
        Retracked.trackComponent(
          _eventData,
          { skillSetFilters: { ...filterSelections, role: value } },
          'skillset_filters_role_selection',
          'change',
          _withTrackingData
        );
      }

      router.replace({
        ...router.location,
        query: {
          ...router.location.query,
          role: value,
        },
      });
    };

    const onSkillsChange = (selectedSkill: string) => {
      if (selectedSkill) {
        Retracked.trackComponent(
          _eventData,
          { skillSetFilters: { ...filterSelections, skills: selectedSkill } },
          'skillset_filters_skills_selection',
          'change',
          _withTrackingData
        );
      }

      router.replace({
        ...router.location,
        query: {
          ...router.location.query,
          skills: selectedSkill,
        },
      });
    };

    const onGoalChange = (value: string) => {
      if (value) {
        Retracked.trackComponent(
          _eventData,
          { skillSetFilters: { ...filterSelections, goal: value } },
          'skillset_filters_goal_selection',
          'change',
          _withTrackingData
        );
      }

      router.replace({
        ...router.location,
        query: {
          ...router.location.query,
          goal: value,
        },
      });
    };

    const onFilterReset = () => {
      router.replace({
        ...router.location,
        query: {
          ...router.location.query,
          role: undefined,
          skills: undefined,
          goal: undefined,
        },
      });
    };

    const onFilterInputChange: FilterInputChangeHandler = (e, type) => {
      // Checking that e is not an empty string because, when a filter option is selected, it results in this event being
      // sent as an empty string whether there was typed input or not. See a more detailed explanation here:
      // https://github.com/webedx-spark/web/pull/15022#discussion_r766820241
      if (e !== '') {
        const propertyName = type + 'Input';
        Retracked.trackComponent(
          _eventData,
          { skillSetFilters: { ...filterSelections, [propertyName]: e } },
          'skillset_filters_input_typing',
          'change',
          _withTrackingData
        );
      }
    };

    const { filteredSkillSets, filterOptions, skillNames, isFilterSelected } = filter(skillSets, filterSelections);
    return (
      <Component
        {...props}
        skillsets={filteredSkillSets}
        filterOptions={filterOptions}
        skillNames={skillNames}
        filterSelections={filterSelections}
        onRoleChange={onRoleChange}
        onSkillsChange={onSkillsChange}
        onGoalChange={onGoalChange}
        onFilterReset={onFilterReset}
        onFilterInputChange={onFilterInputChange}
        isFilterSelected={isFilterSelected}
      />
    );
  };
  return SkillSetFilterHOC0;
};

export const enhancer = compose<Props, PropsFromCaller>(
  setDisplayName('SkillSetFiltersHOC'),
  graphql<
    PropsFromCaller,
    GetAllOccupationsQueryData,
    GetAllOccupationsQueryVariables,
    PropsFromOccupationMetadataQuery
  >(GetAllOccupationsQuery, {
    options: { ssr: false, context: { clientName: 'gatewayGql' } },
    props: ({ data }): PropsFromOccupationMetadataQuery => {
      const occupations = filterExistsOrDefault(data?.Occupations?.getAllOccupations?.elements);
      const allOccupations = occupations.reduce((memo, { id, name }) => {
        // eslint-disable-next-line no-param-reassign
        memo[id] = name;
        return memo;
      }, {} as AllOccupations);
      return { allOccupations };
    },
  }),
  getContext({
    _eventData: PropTypes.object,
    _withTrackingData: PropTypes.func,
  }),
  SkillSetFiltersHOC,
  Retracked.createTrackedContainer<Props>(({ filterSelections }) => ({ skillSetFilters: filterSelections }))
);

export default enhancer;
